import Tokenizr from "tokenizr";
import { hasNoElements } from "./Array";
import { isNotNullOrWhiteSpace, isNullOrWhiteSpace, unescapeAllQuoteTypes } from "./String";
import { ConditionExpression, FilterExpression, LogicalOperator } from "./Filter";

export type TokenSet = [string, string];
type ExpressionTerm = { type: "expr"; value: FilterExpression };
type OperatorTerm = { type: "op"; value: LogicalOperator };
type Term = ExpressionTerm | OperatorTerm;

let lexer: Tokenizr = null;
function getLexer(): Tokenizr {
  if (lexer != null) {
    return lexer;
  }

  lexer = new Tokenizr();

  // ignore all whitespace
  lexer.rule(/\s/i, (ctx) => {
    ctx.ignore();
  });

  lexer.rule(/OR\b/i, (ctx) => {
    ctx.accept(`OR`);
  });

  lexer.rule(/AND\b/i, (ctx) => {
    ctx.accept(`AND`);
  });

  lexer.rule(/\(/i, (ctx) => {
    ctx.accept(`LPAREN`);
  });

  lexer.rule(/\)/i, (ctx) => {
    ctx.accept(`RPAREN`);
  });

  // seperator between field names and values
  lexer.rule(/:/i, (ctx) => {
    ctx.accept(`SEP`);
  });

  // ignore star used in multi-field-searches
  lexer.rule(/\*/i, (ctx) => {
    ctx.accept(`WRD`);
  });

  // ignore injected tokens e.g. {{city}}
  lexer.rule(/{{.*?}}/i, (ctx) => {
    ctx.ignore();
  });

  lexer.rule(/"((?:\\"|\\'|[^\r\n])*)"/, (ctx, match) => {
    ctx.accept(`WRD`, unescapeAllQuoteTypes(match[1]));
  });

  // token enclosed in quotes e.g. "Frankfurt am Main" or '<=2023-02-01T10:15:00.646Z'
  // test: https://regex101.com/r/HG3y8o/2
  lexer.rule(/['"](.*?)['"]/i, (ctx, [, value]) => {
    ctx.accept(`WRD`, value);
  });

  // simple token eg. Hamburg or field names like city
  lexer.rule(/([^:"'\s()]+)/i, (ctx, [, value]) => {
    // Don't match OR as a word if it stands alone
    if (value.toUpperCase() === `OR`) {
      ctx.reject();
      return;
    }

    ctx.accept(`WRD`, value);
  });

  // if nothing matches and token begins with quotes take everything until the end
  // test: https://regex101.com/r/msRStp/2
  lexer.rule(/(['"].*)$/i, (ctx, [, value]) => {
    ctx.accept(`WRD`, value);
  });

  return lexer;
}

export function parseExpression(expression: string): FilterExpression {
  if (isNullOrWhiteSpace(expression)) {
    return { type: `group`, operator: `AND`, expressions: [] };
  }

  const lex = getLexer();
  lex.input(expression);

  return parseExpressionGroup(lex, `AND`);
}

function parseExpressionGroup(lex: Tokenizr, defaultOperator: LogicalOperator): FilterExpression {
  const terms: Term[] = [];
  let currentField = null;
  let seenSeperator = false;

  let token;
  while ((token = lex.token()) !== null) {
    // end of input
    if (token.type === `EOF`) {
      if (currentField != null) {
        terms.push({
          type: `expr`,
          value: {
            type: `condition`,
            fieldName: seenSeperator ? currentField : `*`,
            searchTerm: seenSeperator ? null : currentField
          }
        });

        currentField = null;
      }
      break;
    }

    // skip separators
    if (token.type === `SEP`) {
      if (currentField != null) {
        seenSeperator = true;
      }
      continue;
    }

    // start nested group
    if (token.type === `LPAREN`) {
      terms.push({
        type: `expr`,
        value: parseExpressionGroup(lex, `AND`)
      });

      currentField = null;
      continue;
    }

    // end of current group
    if (token.type === `RPAREN`) {
      break;
    }

    if ([`OR`, `AND`].includes(token.type)) {
      terms.push({ type: `op`, value: token.type });
      continue;
    }

    // unknown token type
    if (token.type !== `WRD`) {
      console.warn(`Unknown token type: ${token.type}`);
      continue;
    }

    // we have a field name
    if (currentField == null) {
      if (seenSeperator && token.value === `*`) {
        continue;
      }

      currentField = token.value;
      continue;
    }

    // we have a field value
    terms.push({
      type: `expr`,
      value: {
        type: `condition`,
        fieldName: currentField,
        searchTerm: token.value
      }
    });

    currentField = null;
    seenSeperator = false;
  }

  // convert implicit ANDs to explicit ANDs
  for (let i = terms.length - 1; i > 0; i--) {
    if (terms[i].type === `expr` && terms[i - 1].type === `expr`) {
      terms.splice(i, 0, { type: `op`, value: `AND` });
    }
  }

  return buildExpressionTree(terms, defaultOperator);
}

function buildExpressionTree(terms: Term[], defaultOp: LogicalOperator = `AND`): FilterExpression {
  // only one term and it's an expression
  if (terms.length === 1 && terms[0].type === `expr`) {
    return terms[0].value;
  }

  // no terms or no expressions
  if (terms.length === 0 || !terms.some((t) => t.type === `expr`)) {
    return {
      type: `group`,
      operator: defaultOp,
      expressions: []
    };
  }

  let i = 1;
  while (i < terms.length) {
    const term = terms[i];
    const isOrOperator = term.type === `op` && term.value === `OR`;
    const isInRange = i - 1 >= 0 && i + 1 < terms.length;
    const isInBetweenExpressions = isInRange && terms[i - 1].type === `expr` && terms[i + 1].type === `expr`;

    if (isOrOperator && isInBetweenExpressions) {
      const orExprs = [terms[i - 1].value, terms[i + 1].value];
      terms.splice(i - 1, 3);

      // Check for additional OR terms
      const isPreviousNotEndOfTerms = i - 1 < terms.length;
      const isPreviousOrOperator = isPreviousNotEndOfTerms && terms[i - 1]?.type === `op` && terms[i - 1]?.value === `OR`;
      const isPreviousOrExpression = isPreviousOrOperator && terms[i]?.type === `expr`;

      while (isPreviousOrExpression) {
        orExprs.push(terms[i].value);
        terms.splice(i - 1, 2);
      }

      terms.splice(i - 1, 0, {
        type: `expr`,
        value: {
          type: `group`,
          operator: `OR`,
          expressions: orExprs as FilterExpression[]
        }
      });

      // restart at front and search for nested groups
      i = 1;
    } else {
      // next operator
      i += 2;
    }
  }

  const andExprs = [];
  for (const term of terms) {
    if (term.type === `expr`) {
      andExprs.push(term.value);
    }
  }

  if (andExprs.length === 1) {
    return andExprs[0];
  }

  return {
    type: `group`,
    operator: `AND`,
    expressions: andExprs
  };
}

export function expressionAsString(expression: FilterExpression): string {
  if (expression == null || (expression.type === `group` && hasNoElements(expression.expressions))) {
    return ``;
  }

  if (expression.type === `condition`) {
    return `${expression.fieldName}:${sanitizeSearch(expression.searchTerm) ?? ``}`;
  }

  let innerExpressions = expression.expressions
    .map((innerExpression) => expressionAsString(innerExpression))
    .filter((s) => isNotNullOrWhiteSpace(s))
    .join(` ${expression.operator} `);

  if (isNullOrWhiteSpace(innerExpressions)) {
    return ``;
  }

  if (expression.operator === `OR`) {
    innerExpressions = `(${innerExpressions})`;
  }

  return innerExpressions;
}

export function sanitizeSearch(search: string): string {
  if (isNullOrWhiteSpace(search)) {
    return search;
  }

  if (/[\s:]/i.test(search)) {
    return `"${search}"`;
  }

  return search;
}

export function mergeExpressions(
  currentExpression: FilterExpression,
  newExpression: FilterExpression
): FilterExpression {
  if (currentExpression == null && newExpression == null) {
    return { type: `group`, operator: `AND`, expressions: [] };
  }

  if (currentExpression == null && newExpression !== null) {
    return newExpression;
  }

  if (currentExpression != null && newExpression == null) {
    return currentExpression;
  }

  if (currentExpression.type === `condition`) {
    return {
      type: `group`,
      operator: `AND`,
      expressions: [currentExpression, newExpression]
    };
  }

  if (currentExpression.type === `group` && hasNoElements(currentExpression.expressions)) {
    return newExpression;
  }

  return {
    type: `group`,
    operator: `AND`,
    expressions: [...currentExpression.expressions, newExpression]
  };
}

export function removeExpression(expression: FilterExpression, fieldName: string, search: string): FilterExpression {
  if (expression == null) {
    return expression;
  }

  if (expression.type === `condition` && expression.fieldName === fieldName && expression.searchTerm === search) {
    return null;
  }

  if (expression.type === `group`) {
    return {
      ...expression,
      expressions: expression.expressions.map((e) => removeExpression(e, fieldName, search)).filter((e) => e != null)
    };
  }

  return expression;
}

export function hasInjectedToken(expression: string): boolean {
  return getInjectedTokenRegExp().test(expression);
}

export function extractInjectedToken(expression: string): string {
  const matches = Array.from(expression.matchAll(getInjectedTokenRegExp()), ([, fieldName]) => `${fieldName}:`);
  return matches[matches.length - 1];
}

const INJECTED_TOKEN_REGEX = /{{(.+?)}}/gi;
function getInjectedTokenRegExp(): RegExp {
  INJECTED_TOKEN_REGEX.lastIndex = -1;
  return INJECTED_TOKEN_REGEX;
}

export function getLastSearchTerm(search: string): ConditionExpression {
  if (isNullOrWhiteSpace(search)) {
    return { type: `condition`, fieldName: null, searchTerm: `` };
  }

  const lastExpression = getLastConditionExpression(parseExpression(search));
  return lastExpression ?? { type: `condition`, fieldName: null, searchTerm: `` };
}

function getLastConditionExpression(expression: FilterExpression): ConditionExpression {
  if (expression == null) {
    return null;
  }

  if (expression.type === `condition`) {
    return expression;
  }

  if (expression.type === `group` && hasNoElements(expression.expressions)) {
    return null;
  }

  const lastExpression = expression.expressions[expression.expressions.length - 1];
  return getLastConditionExpression(lastExpression);
}
